// src/components/UiFeedbackWidget/index.jsx
//
// Quick UI feedback — the floating button on every portal page.
// Spec 20260925-ui-feedback (specs/20260925-ui-idee-assistent/), plan T1.26–T1.30.
// Tasks T045–T050.
//
// ── CLOSED BY DEFAULT (AC-1) ─────────────────────────────────────────────────
// Renders NOTHING unless the user holds BOTH `ideas.ui_feedback` and `ideas.create`.
// The key is in no role default and no template; the pilot grants it per user (V-1).
// FULL_ACCESS_ROLES (admin, company_admin) hold it by bypass — accepted, and announced at
// rollout. The server checks the same key again in createIdea (403).
//
// ── NO AI, ONE REQUEST (AC-11) ───────────────────────────────────────────────
// The only network call is ideasApi.create({ kind: 'ui', body, uiContext }) in ./submit.js.
// A remark becomes an ordinary idea in `submitted` and goes through the same CARO triage
// and PII gate as every other idea before anything could reach the ideabox agent.
//
// ── WHAT IT KNOWS ───────────────────────────────────────────────────────────
// The page comes from CurrentPageContext (Layout publishes useCurrentPage()): a route
// PATTERN, the registry page id, the nav label and the tab. A picked element pins the
// context to the page it was picked on (spec §6.2) until the user unpins.
//
// ── V-12 LIMITATION ─────────────────────────────────────────────────────────
// Feedback about the CONTENT of an open MUI modal dialog is not possible in v1: the
// dialog's backdrop (zIndex.modal) covers the FAB and its focus trap would steal focus
// from this panel. Documented, accepted (plan R-3).
//
// Exposes an imperative `open()` through its ref for the Mod+. shortcut in Layout.

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { UI_FEEDBACK_MIN_BODY } from '@caro/shared/ideas';

import { usePermissions } from '../../contexts/PermissionContext';
import { useCurrentPageContext } from '../../contexts/CurrentPageContext';
import { useBackToTopVisible } from '../BackToTopFab';

import FeedbackFab, { fabBottom } from './FeedbackFab';
import UiFeedbackPanel from './UiFeedbackPanel';
import PickerOverlay from './PickerOverlay';
import { useElementPicker } from './useElementPicker';
import { useUiFeedbackDraft } from './useUiFeedbackDraft';
import { elementIdentity } from './elementIdentity';
import {
  buildUiContext,
  classifyError,
  currentAppVersion,
  currentViewport,
  submitUiFeedback,
} from './submit';

/** The panel's gap above the FAB: 48px FAB + 8px. */
const PANEL_GAP = 56;

const WidgetInner = forwardRef(function WidgetInner({ canViewIdeas }, ref) {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { page: currentPage } = useCurrentPageContext();
  const backToTopVisible = useBackToTopVisible();
  const { draft, update, clear } = useUiFeedbackDraft();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [piiNotice, setPiiNotice] = useState(false);
  const busyRef = useRef(false);
  const inputRef = useRef(null);

  const pinned = Boolean(draft.pinnedPage);
  const page = draft.pinnedPage || currentPage;

  const focusInput = useCallback(() => {
    // After the panel has rendered.
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
  }, []);

  const openPanel = useCallback(() => {
    setOpen(true);
    if (inputRef.current) inputRef.current.focus();
    else focusInput();
  }, [focusInput]);

  useImperativeHandle(ref, () => ({ open: openPanel }), [openPanel]);

  const picker = useElementPicker({
    onPick: (el) => {
      update({ element: elementIdentity(el), pinnedPage: currentPage });
      focusInput();
    },
    onCancel: focusInput,
    locationKey: location.pathname,
  });

  // A page without a route pattern cannot receive feedback. Say so in the console too:
  // a missing pattern is a generator/App.js problem a developer should see.
  useEffect(() => {
    if (open && !(page && page.route)) {
      // eslint-disable-next-line no-console
      console.warn('[ui-feedback] no route pattern for this page; feedback disabled', location.pathname);
    }
  }, [open, page, location.pathname]);

  // The success line fades after a while; the panel stays open for the next remark.
  useEffect(() => {
    if (!success) return undefined;
    const id = setTimeout(() => setSuccess(null), 10000);
    return () => clearTimeout(id);
  }, [success]);

  const close = useCallback(() => {
    if (picker.picking) picker.cancel();
    setOpen(false);
  }, [picker]);

  const submit = useCallback(async () => {
    if (busyRef.current) return;
    setAttempted(true);
    const text = draft.body.trim();
    if (!page || !page.route) return;
    if (text.length < UI_FEEDBACK_MIN_BODY) return;

    busyRef.current = true;
    setBusy(true);
    setError(null);
    setSuccess(null);
    setPiiNotice(false);

    const uiContext = buildUiContext(page, draft.element, draft.categories, {
      viewport: currentViewport(),
      colorMode: theme.palette.mode,
      locale: i18n && i18n.language,
      appVersion: currentAppVersion(),
      pinned,
    });

    try {
      const data = await submitUiFeedback({ body: text, uiContext });
      clear();
      setAttempted(false);
      setSuccess({ publicRef: data && data.publicRef });
      // V-8: a non-blocking notice AFTER sending, only when the server's advisory scan hit.
      setPiiNotice(Boolean(data && Array.isArray(data.piiScanFlags) && data.piiScanFlags.length > 0));
    } catch (e) {
      const c = classifyError(e);
      setError(c);
      if (c.kind === 'invalid') {
        // eslint-disable-next-line no-console
        console.warn('[ui-feedback] refused by the server (422)', c.field);
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [draft, page, pinned, theme.palette.mode, i18n, clear]);

  const toggleCategory = useCallback(
    (c) =>
      update((prev) => ({
        categories: prev.categories.includes(c)
          ? prev.categories.filter((x) => x !== c)
          : [...prev.categories, c],
      })),
    [update],
  );

  const openIdeabox = useCallback(() => {
    // The text travels in NAVIGATION STATE, never in the URL: it may contain anything.
    navigate('/ideas', { state: { newIdeaPrefill: { body: draft.body } } });
  }, [navigate, draft.body]);

  const bottom = fabBottom(backToTopVisible);
  const panelBottom = {
    sm: `calc(${bottom.xs} + ${PANEL_GAP}px)`,
    md: `calc(${bottom.md} + ${PANEL_GAP}px)`,
  };

  return (
    <>
      <FeedbackFab
        open={open}
        backToTopVisible={backToTopVisible}
        onClick={() => (open ? close() : openPanel())}
      />
      {open ? (
        <UiFeedbackPanel
          page={page}
          pinned={pinned}
          onUnpin={() => update({ pinnedPage: null, element: null })}
          body={draft.body}
          onBodyChange={(v) => update({ body: v })}
          categories={draft.categories}
          onToggleCategory={toggleCategory}
          element={draft.element}
          onClearElement={() => update({ element: null, pinnedPage: null })}
          onStartPick={picker.start}
          picking={picker.picking}
          onSubmit={submit}
          onClose={close}
          busy={busy}
          attempted={attempted}
          error={error}
          success={success}
          piiNotice={piiNotice}
          canViewIdeas={canViewIdeas}
          onOpenIdeabox={openIdeabox}
          inputRef={inputRef}
          bottom={panelBottom}
        />
      ) : null}
      <PickerOverlay picking={picker.picking} rect={picker.rect} />
    </>
  );
});

/** The gate. Nothing — no hook, no listener, no DOM — without both permissions. */
const UiFeedbackWidget = forwardRef(function UiFeedbackWidget(_props, ref) {
  const { hasPermission } = usePermissions();
  const allowed = hasPermission('ideas.ui_feedback') && hasPermission('ideas.create');
  if (!allowed) return null;
  return <WidgetInner ref={ref} canViewIdeas={hasPermission('ideas.view')} />;
});

export default UiFeedbackWidget;
